import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { AccountCircle, HeadsetMic } from '@mui/icons-material';
import { useGuest } from 'hooks/useGuest';
import { consumeSupportIntent, markSupportIntentBeforeLogin } from 'utils/auth';
import { GUEST_CTA } from 'utils/guestCta';

import { CashierContext } from 'components/Cashier/CashierContext';
import GuestCtaButton from 'components/LoginPrompt/GuestCtaButton';
import { promptLogin } from 'components/LoginPrompt/loginPromptStore';
import { InboxIcon } from 'components/Notifications/NotificationList';
import { useNotifications } from 'components/Notifications/useNotifications';
import AccountMenuPanel from 'components/Profile/AccountMenuPanel';
import { useCompactLobbyHeader } from 'components/SaloonLobby/compactHeader';
import MediaImage from 'components/SaloonLobby/MediaImage';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import { useOwnStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';
import { useSupportContext } from 'components/Support/SupportContext';

import styles from './SaloonHeader.module.scss';

interface SaloonHeaderProps {
  cashBalance: number;
  promoBalance: number;
  avatar: string;
  /**
   * The promo ribbon, when the header is wide enough to carry it (owner,
   * 2026-09-09; SaloonLobby decides via useWideLobbyHeader). Rides after the
   * wordmark, left-aligned, inside the band's existing height.
   */
  promoSlot?: React.ReactNode;
  /**
   * Tap the wordmark to go home — the Cash tab, or the top of it if that is
   * already where you are (owner, 2026-09-11). The lobby owns both behaviours
   * because it owns the tab and the scroll container; the header only reports
   * the tap. Absent, the wordmark stays the plain image it was.
   */
  onHomeClick?: () => void;
}

// Always cents, for BOTH figures (owner, 2026-09-08): "$125.00", not "$125" —
// the promo balance is money like the cash balance and reads like it.
const formatCurrency = (value: number): string =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const SaloonHeader: React.FC<SaloonHeaderProps> = ({
  cashBalance,
  promoBalance,
  avatar,
  onHomeClick,
  promoSlot
}) => {
  const { openCashier, closeCashier } = useContext(CashierContext);
  const {
    openPanel: openSupport,
    closePanel: closeSupport,
    unreadCount: supportUnread
  } = useSupportContext();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // Which view the account menu opens to: the avatar opens the root menu, the
  // envelope opens the Notifications tab directly (no separate modal).
  const [menuInitialView, setMenuInitialView] = useState<'notifications' | 'profile' | null>(null);
  const { unreadCount } = useNotifications();
  // The streak frame around the profile button (EN-11164), as in the account
  // panel; the button's own orange ring steps aside while it is worn.
  const streakFrame = useOwnStreakFrame();
  const compactHeader = useCompactLobbyHeader();

  // Guest (not-logged-in): the member controls (balance/cashier, notifications,
  // support, profile) are meaningless, so the header shows Log in / Sign up
  // instead — the lobby's only call to action.
  const guest = useGuest();

  // CASH / PROMO words: shown whenever the row can hold the wordmark and the
  // full pill; dropped only when it cannot (the wordmark would run into the
  // pill). Measured, not a breakpoint — the balances' length decides as much
  // as the viewport does. The words' width is remembered while they are shown
  // so the row can tell when there is room to bring them back; both decisions
  // compare the same "with words" width, so the state cannot flap.
  const rowRef = useRef<HTMLDivElement>(null);
  const clusterRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const labelsFitRef = useRef(true);
  const labelsExtraRef = useRef(0);
  const [labelsFit, setLabelsFit] = useState(true);
  useLayoutEffect(() => {
    if (guest) return undefined;
    const row = rowRef.current;
    const cluster = clusterRef.current;
    const pill = pillRef.current;
    if (!row || !cluster || !pill) return undefined;
    const gapOf = (el: Element) => parseFloat(getComputedStyle(el).columnGap) || 0;
    const widthOf = (el: Element) => el.getBoundingClientRect().width;
    // getBoundingClientRect stops at the border box, so a label's own margin is
    // invisible to it. `.balanceLabel` carries margin-right, and leaving it out
    // made the remembered "with words" width ~10px short across the two labels —
    // enough, at the boundary, to bring the words back into a row that cannot
    // hold them and let the pill run under the wordmark (review, 2026-09-12).
    const marginXOf = (el: Element) => {
      const cs = getComputedStyle(el);
      return (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
    };
    const measure = () => {
      if (labelsFitRef.current) {
        const pillGap = gapOf(pill);
        labelsExtraRef.current = Array.from(
          pill.querySelectorAll<HTMLElement>('[data-balance-label]')
        ).reduce((sum, el) => sum + widthOf(el) + marginXOf(el) + pillGap, 0);
      }
      const children = Array.from(cluster.children);
      const clusterWidth =
        children.reduce((sum, el) => sum + widthOf(el), 0) +
        gapOf(cluster) * Math.max(0, children.length - 1) +
        (labelsFitRef.current ? 0 : labelsExtraRef.current);
      const wordmark = row.querySelector('img');
      const needed = (wordmark ? widthOf(wordmark) : 0) + gapOf(row) + clusterWidth;
      const fits = needed <= row.clientWidth + 0.5;
      if (fits !== labelsFitRef.current) {
        labelsFitRef.current = fits;
        setLabelsFit(fits);
      }
    };
    measure();
    // The row's width is only half the story: the pill grows when the web font
    // lands and the wordmark gains its width when the image loads, neither of
    // which resizes the row (the cluster overflows instead). Watch them too, or
    // a phone measured before its font arrived keeps the words and the pill
    // runs under the wordmark.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(pill);
    const wordmark = row.querySelector('img');
    if (wordmark) observer.observe(wordmark);
    (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready?.then(measure);
    return () => observer.disconnect();
    // `[guest]` only. The balances used to be deps too, which re-ran the whole
    // effect — observers torn down and rebuilt, four getComputedStyle reads and
    // a forced layout — on EVERY balance tick, on a surface that already
    // re-renders per socket tick (review, 2026-09-16). But a balance only
    // matters here through the pill's WIDTH, and the observer on the pill
    // already reports that: a tick that changes no width has nothing to
    // measure, and one that does fires the observer. The trade is timing at
    // the exact boundary — a tick that flips the fit now lands through the
    // observer, a frame after the text, instead of in this layout effect —
    // which is the path a resize or a late web font always took.
  }, [guest]);

  // Header panels (Cashier / Support / Profile) are mutually exclusive — opening
  // one always closes the others, so users never see two overlapping fly-outs.
  const handleOpenCashier = useCallback(() => {
    setShowProfileMenu(false);
    closeSupport();
    openCashier();
  }, [openCashier, closeSupport]);

  const handleOpenSupport = useCallback(() => {
    // Guests can't use member support — advise them to log in instead of
    // opening the support panel.
    if (guest) {
      markSupportIntentBeforeLogin();
      promptLogin({
        title: 'Log in to contact support',
        message:
          "You're browsing as a guest. Log in or create an account to contact our support team.",
        dismissLabel: 'Not now'
      });
      return;
    }
    setShowProfileMenu(false);
    closeCashier();
    openSupport();
  }, [guest, openSupport, closeCashier]);

  // A guest who chose to log in from the support advisory (markSupportIntentBeforeLogin
  // above) lands back on whichever page they were on, authenticated. Open the
  // support panel for them instead of making them find this button again.
  useEffect(() => {
    if (guest) return;
    if (!consumeSupportIntent()) return;
    setShowProfileMenu(false);
    closeCashier();
    openSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest]);

  const handleToggleProfile = useCallback(() => {
    setShowProfileMenu((prev) => {
      if (!prev) {
        closeCashier();
        closeSupport();
        setMenuInitialView(null);
      }
      return !prev;
    });
  }, [closeCashier, closeSupport]);

  // Envelope icon → open the account menu straight to its Notifications tab.
  const handleOpenNotifications = useCallback(() => {
    closeCashier();
    closeSupport();
    setMenuInitialView('notifications');
    setShowProfileMenu(true);
  }, [closeCashier, closeSupport]);

  // Allow other components to programmatically open the profile panel
  // (e.g. expired-token banner, account-completion CTA).
  useEffect(() => {
    const handleOpenProfilePanel = (event: Event) => {
      const customEvent = event as CustomEvent<{ field?: string }>;
      const fieldToFocus = customEvent.detail?.field;

      closeCashier();
      closeSupport();
      setMenuInitialView(fieldToFocus ? 'profile' : null);
      setShowProfileMenu(true);

      if (fieldToFocus) {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('focus-profile-field', { detail: { field: fieldToFocus } })
          );
        }, 0);
      }
    };

    window.addEventListener('open-profile-panel', handleOpenProfilePanel);
    return () => window.removeEventListener('open-profile-panel', handleOpenProfilePanel);
  }, [closeCashier, closeSupport]);

  return (
    <header className={styles.header}>
      <div className={styles.row} ref={rowRef}>
        {/* A real <button>, not a click handler on the image: it is a control,
            so it must be reachable by keyboard and announce itself. The label
            says where it goes, because "Hijack Poker" alone does not.
            The measurement pass above reads `row.querySelector('img')` for the
            wordmark's width — the img is still there, one level down, so that
            keeps working. */}
        <button
          type='button'
          className={styles.wordmarkButton}
          onClick={onHomeClick}
          aria-label='Hijack Poker — go to Cash games'>
          <MediaImage
            path='images/lobby/hijack-wordmark-v1.png'
            alt=''
            className={styles.wordmark}
          />
        </button>

        {/* The promo ribbon rides here on wide screens — after the wordmark, on
            the same leading edge, inside the band's height. Not measured by the
            balance-label collision check: it yields first (flex-shrink), so the
            wordmark and the cluster keep their room. */}
        {promoSlot && <div className={styles.promoSlot}>{promoSlot}</div>}

        {/* Everything after the wordmark: on phones the balance pill grows to
            fill this cluster (the production lobby's layout); on desktop the
            cluster hugs the trailing edge. */}
        <div className={styles.cluster} ref={clusterRef}>
          {guest ? (
            <div style={guestActionsStyle}>
              <GuestCtaButton label={GUEST_CTA.header} className={styles.guestCta} />
              {/* Support tap shows a login advisory for guests (handleOpenSupport). */}
              <button
                type='button'
                className={styles.iconButton}
                onClick={handleOpenSupport}
                aria-label='Support'>
                <HeadsetMic fontSize='small' />
                {supportUnread > 0 && (
                  <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
                )}
              </button>
            </div>
          ) : (
            <>
              <button
                type='button'
                ref={pillRef}
                className={`${styles.balancePill} ${labelsFit ? '' : styles.balancePillCompact}`}
                onClick={handleOpenCashier}
                aria-label={`Open cashier. Cash balance ${formatCurrency(
                  cashBalance
                )}, promo balance ${formatCurrency(promoBalance)}`}>
                {/* EN-10516: these are two different KINDS of money. They were
                  distinguished only by colour and a thin pipe, which reads as
                  one figure at a glance, fails for colour-blind players, and
                  said neither word to a screen reader. Persistent text labels
                  carry the distinction without relying on colour. */}
                <span className={styles.balanceLabel} data-balance-label aria-hidden>
                  CASH
                </span>
                <span className={styles.cashAmount}>{formatCurrency(cashBalance)}</span>
                <span className={styles.separator}>|</span>
                <span className={styles.balanceLabel} data-balance-label aria-hidden>
                  PROMO
                </span>
                <span className={styles.promoAmount}>{formatCurrency(promoBalance)}</span>
              </button>

              {/* Envelope opens the account menu's Notifications tab directly
                (no separate notification modal — see EN-9838). Unread dot mirrors
                the same store the menu list reads. */}
              <button
                type='button'
                className={styles.iconButton}
                onClick={handleOpenNotifications}
                aria-label={
                  unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
                }>
                <InboxIcon />
                {unreadCount > 0 && <span style={notifBadgeStyle} aria-hidden='true' />}
              </button>

              <button
                type='button'
                className={styles.iconButton}
                onClick={handleOpenSupport}
                aria-label='Support'>
                <HeadsetMic fontSize='small' />
                {supportUnread > 0 && (
                  <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
                )}
              </button>

              <AvatarFrame tier={streakFrame} size={compactHeader ? 36 : 42}>
                <button
                  type='button'
                  className={`${styles.avatar} ${streakFrame ? styles.avatarFramed : ''}`}
                  onClick={handleToggleProfile}
                  aria-label='Player profile'>
                  {avatar ? (
                    <MediaImage
                      path={`images/${avatar}`}
                      alt='Player avatar'
                      className={styles.avatarImage}
                    />
                  ) : (
                    // Same fallback as the grid header's profile button, so a player
                    // without a photo sees one marker in both rooms.
                    <AccountCircle className={styles.avatarFallback} />
                  )}
                </button>
              </AvatarFrame>
            </>
          )}
        </div>
      </div>

      {!guest && (
        <AccountMenuPanel
          open={showProfileMenu}
          onClose={() => setShowProfileMenu(false)}
          initialView={menuInitialView}
        />
      )}
    </header>
  );
};

export default SaloonHeader;

const guestActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10
};

const supportBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  minWidth: '16px',
  height: '16px',
  borderRadius: '8px',
  background: '#C42021',
  color: '#fff',
  fontSize: '0.55rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 3px',
  border: '1.5px solid rgba(15, 25, 35, 0.95)'
};

// Small unread dot on the notifications envelope (mirrors NotificationCenter's
// old dot — surfaces "unread, here" without a numeric count).
const notifBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: '6px',
  right: '6px',
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: '#e8821f',
  boxShadow: '0 0 6px rgba(232, 130, 31, 0.8), 0 0 0 1.5px rgba(15, 25, 35, 0.95)',
  pointerEvents: 'none'
};
